import { useEffect, useRef } from "react";
import { siteConfig } from "@/lib/site-config";
import { useFirstInteraction } from "@/hooks/useFirstInteraction";

interface FormEmbedProps {
  id?: string;
  className?: string;
  /**
   * Height reserved for the embed before it loads, so deferring the iframe and
   * the skeleton→form swap cause no layout shift. Roughly the settled height of
   * the LeadConnector form.
   */
  reserveHeight?: number;
}

/**
 * Embeds the LeadConnector form widget. The iframe pulls in reCAPTCHA, a
 * Facebook pixel and its own web fonts (~1.9MB, ~1s of reCAPTCHA CPU), so it is
 * deferred until the visitor's first interaction ([[useFirstInteraction]]). A
 * form-shaped skeleton holds the reserved space until then; a form opened from a
 * click renders instantly because the click is itself the interaction. We never
 * force the iframe height — form_embed.js controls it via postMessage.
 */
const FormEmbed = ({
  id = `inline-${siteConfig.widgets.formId}`,
  className = "",
  reserveHeight = 560,
}: FormEmbedProps) => {
  const interacted = useFirstInteraction();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!interacted) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [interacted]);

  return (
    <div ref={containerRef} className={className} style={{ minHeight: reserveHeight }}>
      {interacted ? (
        <iframe
          src={siteConfig.widgets.formIframeSrc}
          style={{ width: "100%", border: "none", borderRadius: 10, display: "block", minHeight: reserveHeight }}
          id={id}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={siteConfig.widgets.formName}
          data-height="720"
          data-layout-iframe-id={id}
          data-form-id={siteConfig.widgets.formId}
          title={siteConfig.widgets.formName}
          scrolling="no"
        />
      ) : (
        <div
          className="flex flex-col gap-4"
          style={{ minHeight: reserveHeight }}
          aria-hidden="true"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-11 w-full rounded-lg bg-muted" />
            </div>
          ))}
          <div className="space-y-2">
            <div className="h-3 w-28 rounded bg-muted" />
            <div className="h-24 w-full rounded-lg bg-muted" />
          </div>
          <div className="mt-1 h-12 w-full rounded-lg bg-primary/25" />
        </div>
      )}
    </div>
  );
};

export default FormEmbed;
